import React, { lazy, Suspense } from 'react'
const StyledList = lazy(() => import('../Atoms/StyledList'))
const Animation = lazy(
  () => import('@toasttab/shared/src/components/atoms/Animation/Animation')
)
const Typography = lazy(
  () => import('@toasttab/shared/src/components/atoms/Typography/Typography')
)
const DescriptionMDS = lazy(
  () =>
    import(
      '@toasttab/shared/src/components/molecules/DescriptionMDS/DescriptionMDS'
    )
)
const TextLink = lazy(
  () => import('@toasttab/shared/src/components/molecules/TextLink/TextLink')
)
const ImageResolutions = lazy(
  () => import('@toasttab/shared/src/components/atoms/Image/ImageResolutions')
)
const Accordion = lazy(
  () => import('@toasttab/shared/src/components/molecules/Accordion/Accordion')
)
const Eyebrow = lazy(
  () => import('@toasttab/shared/src/components/molecules/Eyebrow/Eyebrow')
)
const Video = lazy(
  () => import('@toasttab/shared/src/components/atoms/Video/Video')
)
const Button = lazy(
  () => import('@toasttab/shared/src/components/atoms/Button/Button')
)

interface ContentColumnProps {
  type: string
  component: any
  textColor?: string
}

const Components: any = {
  styledList: StyledList,
  animation: Animation,
  heading: Typography,
  description: DescriptionMDS,
  link: TextLink,
  logo: ImageResolutions,
  button: Button,
  image: ImageResolutions,
  accordion: Accordion,
  eyebrow: Eyebrow,
  video: Video
}

const getBaseProps = (type: string, component: any, textColor: string) => {
  switch (type) {
    case 'heading':
      return {
        as: component.as || 'h2',
        variation: component.variation || 'h2',
        classes: `${
          component.classes ? component.classes : 'mb-6 '
        } ${textColor}`
      }
    case 'description':
      return {
        tagEl: 'div',
        variation: 'default',
        classes: `${component.classes ? component.classes : 'mb-4'}` // textColorClass
      }
    case 'eyebrow':
      return {
        variation: 'ds',
        classes: 'overline-text inline-flex items-center mb-2'
      }
    case 'styledList':
      return {
        classes: ''
      }
    case 'video':
      return {
        classes: component.classes
      }
    default:
      return {}
  }
}

const refactorProps = (type: string, component: any) => {
  switch (type) {
    case 'description':
      return {
        content: component.wysiwygV2
      }
    case 'link':
      return {
        href: component.ctaLink,
        text: component.ctaLabel,
        icon: component.linkIcons,
        color: 'default' // text-color
      }
    case 'button':
      return {
        href: component.ctaLink,
        variation: component.buttonVariations,
        size: component.buttonPadding,
        fullWidth: component.buttonPadding === 'Full Width'
      }
    case 'image':
      return {
        ...component,
        images: component,
        borderRadius: component.imageResolutions[0].borderRadius?.label || null
      }
    default:
      return { ...component }
  }
}

const addChildComponents = (type: string, component: any) => {
  switch (type) {
    case 'accordion':
      return component.children
    case 'button':
      return component.ctaLabel
    case 'heading':
      return component.sectionHeading
    default:
      return {}
  }
}

const ContentColumn = ({ type, component, textColor }: ContentColumnProps) => {
  const componentBaseProps = getBaseProps(type, component, textColor)
  const componentProps = refactorProps(type, component)
  const componentChildren = addChildComponents(type, component)
  if (typeof Components[type] !== 'undefined') {
    return (
      <Suspense fallback={null}>
        {React.createElement(
          Components[type],
          {
            ...componentBaseProps,
            ...componentProps
          },
          componentChildren
        )}
      </Suspense>
    )
  }
}

export default ContentColumn
